import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Badge, Tag, Typography } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  LinkOutlined,
  MailOutlined,
  PaperClipOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { getDefaults } from '../../services/detailDefaults';
import { currencySymbol, formatAmount, toNumber } from '../../utils/currency';
import DocumentViewer from '../DocumentViewer';
import { displayPhoneForField } from './phoneDisplay';
import { buildFileUrl, extractDocuments } from './renderConfig';
import '../../styles/FileField.css';

const { Text } = Typography;

// ════════════════════════════════════════════════════════════════════════════
//  GENERIC DETAIL-VIEW RENDER ENGINE
//  One renderer for every detail page. Each field carries a `renderType` (set
//  from Admin → Detail Groups, stored in the detailViewConfig registry) plus
//  optional `renderOptions`. Adding or switching a supported render type is a
//  config change — no frontend deploy required.
//
//  Backward compatible: when `renderType` is absent we fall back to the legacy
//  `documentPreview` / `displayAs` flags, then to the original auto behaviour.
// ════════════════════════════════════════════════════════════════════════════

// ── value helpers ────────────────────────────────────────────────────────────
const fvIsEmpty = (v) => v === undefined || v === null || v === '';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;

function toTitleCase(str) {
  return String(str).replace(/\w\S*/g, (t) => {
    if (t.length > 1 && t === t.toUpperCase()) return t; // keep acronyms (VMS, C2H)
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  });
}

function shouldTitleCase(str) {
  if (typeof str !== 'string' || !str.trim()) return false;
  if (!/[a-z]/i.test(str)) return false;
  if (str.includes('@')) return false;
  if (/^https?:\/\//i.test(str)) return false;
  if (/^[a-f0-9]{24}$/i.test(str)) return false;
  if (/<[a-z][\s\S]*>/i.test(str)) return false;
  return true;
}

function smartText(v, capitalize) {
  if (fvIsEmpty(v)) return '';
  const s = String(v);
  if (capitalize === false) return s;
  return shouldTitleCase(s) ? toTitleCase(s) : s;
}

function objectToText(obj, capitalize, separator) {
  return Object.values(obj)
    .filter((x) => !fvIsEmpty(x) && typeof x !== 'object')
    .map((x) => smartText(x, capitalize))
    .join(separator);
}

// applyTransform applies an explicit textTransform render option.
function applyTransform(s, transform) {
  if (!s) return s;
  switch (transform) {
    case 'upper': return String(s).toUpperCase();
    case 'lower': return String(s).toLowerCase();
    case 'title': return toTitleCase(s);
    default: return s;
  }
}

const isReference = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'id' in v;

// toScalarString flattens any value into a single display string.
function toScalarString(value, capitalize, separator) {
  if (fvIsEmpty(value)) return '';
  if (isReference(value)) return smartText(value.value ?? value.id, capitalize);
  if (Array.isArray(value)) {
    return value
      .map((it) => (isReference(it) ? smartText(it.value ?? it.id, capitalize)
        : it && typeof it === 'object' ? objectToText(it, capitalize, separator)
          : smartText(it, capitalize)))
      .filter(Boolean)
      .join(separator);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return objectToText(value, capitalize, separator);
  return smartText(value, capitalize);
}

// toItemList splits arrays / comma-separated strings into individual tag items.
function toItemList(value, capitalize, separator) {
  if (Array.isArray(value)) {
    return value
      .map((it) => (isReference(it) ? smartText(it.value ?? it.id, capitalize)
        : it && typeof it === 'object' ? objectToText(it, capitalize, separator)
          : smartText(it, capitalize)))
      .filter((t) => t !== '');
  }
  if (typeof value === 'string') {
    return value.split(',').map((s) => smartText(s.trim(), capitalize)).filter(Boolean);
  }
  const s = toScalarString(value, capitalize, separator);
  return s ? [s] : [];
}

// ── file chip icon ───────────────────────────────────────────────────────────
function FileChipIcon({ name }) {
  const ext = String(name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FilePdfOutlined className="v1-file-chip__icon" />;
  if (ext === 'doc' || ext === 'docx') return <FileWordOutlined className="v1-file-chip__icon" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return <FileImageOutlined className="v1-file-chip__icon" />;
  if (['txt', 'csv', 'json', 'md'].includes(ext)) return <FileTextOutlined className="v1-file-chip__icon" />;
  return <FileOutlined className="v1-file-chip__icon" />;
}
FileChipIcon.propTypes = { name: PropTypes.string };

// resolveRenderType: explicit renderType wins; otherwise map the legacy flags.
function resolveRenderType(field) {
  if (field?.renderType && field.renderType !== 'auto') return field.renderType;
  if (field?.documentPreview === true) return 'document';
  // Any phone-typed field (or one the form masks with formatter:'phone') renders
  // through the common phone formatter — one format for every contact number.
  if (field?.formatter === 'phone' || field?.type === 'phone') return 'phone';
  if (field?.displayAs === 'tag') return 'tag';
  if (field?.displayAs === 'text') return 'text';
  return 'auto';
}

// Phone formatting (mask + this record's own country code) lives in
// ./phoneDisplay.js — see that file for why the code can never come from a
// global default alone.

// ── render-type implementations ──────────────────────────────────────────────
function DocumentRender({ value, mode, empty, style }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const docs = extractDocuments(value);
  if (!docs.length) return <Text strong className="v1-detail-value" style={style}>{empty}</Text>;
  const previewable = docs.filter((d) => d.url);

  if (mode === 'text') {
    return <Text strong className="v1-detail-value" style={style}>{docs.map((d) => d.name).join(', ')}</Text>;
  }
  if (mode === 'tag') {
    return (
      <span className="v1-detail-value v1-detail-tags">
        {docs.map((d, i) => <Tag key={i} icon={<PaperClipOutlined />}>{d.name}</Tag>)}
      </span>
    );
  }
  if (mode === 'link' || mode === 'download') {
    return (
      <span className="v1-detail-value v1-doc-list">
        <span className="v1-file-list">
          {docs.map((d, i) => (d.url ? (
            <a
              key={i}
              className="v1-file-chip"
              href={d.url}
              target="_blank"
              rel="noopener noreferrer"
              download={mode === 'download' ? (d.name || '') : undefined}
              title={d.name}
            >
              <FileChipIcon name={d.name} />
              <span className="v1-file-chip__name">{d.name}</span>
              {mode === 'download' ? <DownloadOutlined className="v1-file-chip__action" /> : <LinkOutlined className="v1-file-chip__action" />}
            </a>
          ) : (
            <span key={i} className="v1-file-chip v1-file-chip--disabled" title="No file location"><FileChipIcon name={d.name} /><span className="v1-file-chip__name">{d.name}</span></span>
          )))}
        </span>
      </span>
    );
  }

  // default: preview (slider viewer)
  return (
    <div className="v1-detail-value v1-doc-list">
      <div className="v1-file-list">
        {docs.map((d, i) => (d.url ? (
          <button type="button" key={i} className="v1-file-chip" onClick={() => { setIndex(previewable.indexOf(d)); setOpen(true); }} title={`Preview ${d.name}`}>
            <FileChipIcon name={d.name} />
            <span className="v1-file-chip__name">{d.name}</span>
            <EyeOutlined className="v1-file-chip__action" />
          </button>
        ) : (
          <span key={i} className="v1-file-chip v1-file-chip--disabled" title="No preview available for this file"><FileChipIcon name={d.name} /><span className="v1-file-chip__name">{d.name}</span></span>
        )))}
      </div>
      <DocumentViewer documents={previewable} open={open} onClose={() => setOpen(false)} initialIndex={index} />
    </div>
  );
}

DocumentRender.propTypes = {
  value: PropTypes.any,
  mode: PropTypes.string,
  empty: PropTypes.string,
  style: PropTypes.object,
};

// formatCurrency — through the SHARED money helpers (utils/currency.js).
//
// This used to parse with `Number(String(value).replace(/[^0-9.-]/g, ''))`,
// which stripped the "e" and the "+" out of an exponent. Go's encoding/json
// marshals a large float64 as "1.2e+07", so a 12,000,000 client rate arrived as
// that string and was silently rendered as 1.207 — a plausible-looking number
// that was wrong by seven orders of magnitude. toNumber is exponent-safe, and
// formatAmount never emits exponent notation back.
function formatCurrency(value, opts = {}) {
  if (Number.isNaN(toNumber(value))) return String(value ?? '');
  // `currencySymbol` on renderOptions may hold either a symbol or an ISO code;
  // normalising here means a field configured with "USD" no longer prints
  // "USD12,000,000". An unset option keeps rendering the bare amount.
  const symbol = opts.currencySymbol ? currencySymbol(opts.currencySymbol, '') : '';
  return `${symbol}${formatAmount(value, { decimals: opts.decimals })}`;
}

// ── main component ───────────────────────────────────────────────────────────
// `record` is the flattened value map of the record being rendered (or, inside a
// repeatable group, that ROW). Render types that need a SIBLING value read it
// from here — today the phone renderer's country code, resolved per record
// instead of from one global default.
export default function FieldValue({ field, record }) {
  const { value, type, prefix = '', suffix = '', color = '', capitalize } = field ?? {};
  const opts = field?.renderOptions ?? {};
  const defaults = getDefaults();
  const empty = defaults.emptyPlaceholder;
  const sep = defaults.separator || ', ';
  const style = color ? { color } : undefined;
  const tagColor = opts.tagColor || color || undefined;
  const transform = opts.textTransform && opts.textTransform !== 'none' ? opts.textTransform : null;

  const wrap = (txt) => `${prefix ? `${prefix} ` : ''}${txt}${suffix ? ` ${suffix}` : ''}`;
  const finish = (s) => (transform ? applyTransform(s, transform) : s);
  const asText = (s) => <Text strong className="v1-detail-value" style={style}>{finish(s)}</Text>;
  // An ATOMIC value (a date, a phone number, an amount) must never wrap inside
  // itself — CSS would happily break "873-487-3847" after the hyphen, and a
  // number shown as "873-" reads as corrupted data. Prose values keep wrapping.
  const asToken = (s) => <Text strong className="v1-detail-value v1-detail-token" style={style}>{finish(s)}</Text>;
  const asTagEl = (s) => <span className="v1-detail-value v1-detail-tags"><Tag color={tagColor}>{finish(s)}</Tag></span>;

  const renderType = resolveRenderType(field);

  // Documents handle their own emptiness; everything else short-circuits on empty
  // unless the field explicitly opts to show empty values.
  if (renderType !== 'document' && fvIsEmpty(value) && !opts.showEmpty) {
    return asText(empty);
  }

  switch (renderType) {
    case 'document':
      return <DocumentRender value={value} mode={opts.documentMode || 'preview'} empty={empty} style={style} />;

    case 'text':
      return asText(wrap(toScalarString(value, capitalize, sep)));

    case 'tag':
    case 'chip':
      return asTagEl(wrap(toScalarString(value, capitalize, sep)));

    case 'badge': {
      const s = finish(toScalarString(value, capitalize, sep));
      return <span className="v1-detail-value"><Badge color={tagColor || 'blue'} text={s} /></span>;
    }

    case 'tags':
    case 'multitag':
    case 'skills': {
      const items = toItemList(value, capitalize, sep);
      if (!items.length) return asText(empty);
      return (
        <span className="v1-detail-value v1-detail-tags">
          {items.map((t, i) => <Tag key={i} color={tagColor}>{finish(t)}</Tag>)}
        </span>
      );
    }

    case 'link': {
      const raw = toScalarString(value, false, sep);
      const href = /^https?:\/\//i.test(raw) ? raw : buildFileUrl(raw) || raw;
      return <a className="v1-detail-value v1-doc-link" href={href} target="_blank" rel="noopener noreferrer">{finish(wrap(raw))}</a>;
    }

    case 'email': {
      const raw = toScalarString(value, false, sep);
      return <a className="v1-detail-value" href={`mailto:${raw}`}><MailOutlined /> {raw}</a>;
    }

    case 'phone': {
      const raw = toScalarString(value, false, sep);
      const shown = displayPhoneForField(field, raw, record);
      return <a className="v1-detail-value v1-detail-token" href={`tel:${raw.replace(/[^0-9+]/g, '')}`}><PhoneOutlined /> {finish(wrap(shown))}</a>;
    }

    case 'date': {
      const d = dayjs(value);
      const fmt = opts.dateFormat || defaults.dateFormat;
      return asToken(wrap(d.isValid() ? d.format(fmt) : toScalarString(value, capitalize, sep)));
    }

    case 'currency':
      return asToken(wrap(formatCurrency(value, opts)));

    case 'budget': {
      // currency value + optional unit mapped via budgetUnitMap (Monthly → mon).
      const money = formatCurrency(value, opts);
      const unitRaw = opts.unit ? String(opts.unit) : '';
      const unit = unitRaw && opts.budgetUnitMap ? (opts.budgetUnitMap[unitRaw] ?? unitRaw) : unitRaw;
      return asToken(`${money}${unit ? `/${unit}` : ''}`);
    }

    case 'html':
      return (
        <span
          className="v1-detail-value"
          style={style}
          // Content comes from the tenant's own records (same trust level as the
          // existing DescriptionBlock); rendered only when the admin opts in.
          dangerouslySetInnerHTML={{ __html: String(value ?? '') }}
        />
      );

    default:
      break;
  }

  // ── 'auto' — original behaviour (kept verbatim for backward compatibility) ──
  if (fvIsEmpty(value)) return asText(empty);

  const isDateField = type === 'date';
  const isDateString = typeof value === 'string' && ISO_DATE_RE.test(value.trim());
  if (isDateField || isDateString) {
    const d = dayjs(value);
    if (d.isValid()) return asText(wrap(d.format(defaults.dateFormat)));
  }
  if (isReference(value)) return asText(wrap(smartText(value.value ?? value.id, capitalize)));
  if (Array.isArray(value)) {
    const items = toItemList(value, capitalize, sep);
    if (!items.length) return asText(empty);
    return (
      <span className="v1-detail-value v1-detail-tags">
        {items.map((t, i) => <Tag key={i} color={tagColor}>{t}</Tag>)}
      </span>
    );
  }
  if (typeof value === 'boolean') return asText(value ? defaults.booleanTrueLabel : defaults.booleanFalseLabel);
  if (typeof value === 'object') return asText(objectToText(value, capitalize, sep) || empty);
  return asText(wrap(smartText(value, capitalize)));
}

FieldValue.propTypes = { field: PropTypes.object, record: PropTypes.object };
